import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import GUI from "../GUI"
import Select, { TPrimitiveId } from "../Select/Select"
import { useOverlay } from "../Overlay/OverlayProvider"
import Menu, { IMenu, TMenuProps, TMenuRow } from "../Menu/Menu"
import CloseIcon from "../Icons/CloseIcon"
import IconButton from "../Button/IconButton"
import ChevronRight from "../Icons/ChevronRight"
import { TAnchorProps } from "../Overlay/AnchoredBackdrop"
import Timeout from "../../libs/observer/Timeout"
import { UpdateEvent } from "../GUIEvents"
import { useFormControl } from "../Form/FormControl"

export type TDropdownProps<ID extends TPrimitiveId> = TMenuProps<ID> & {
    valueRenderer?:(option:TMenuRow<ID>) => React.ReactNode
    placeholder?:string
    float?:boolean
    canClear?:boolean
    hideArrow?:boolean,
    anchorProps?:Partial<TAnchorProps>
    onDismissed?:Function
    readOnly?:boolean

    // form
    onValid?:(valid:boolean) => void
    onDirty?:(dirty:boolean) => void
}

export interface IDropdown<ID extends TPrimitiveId> {
    value:ID|undefined
    open:Function
    close:Function

    // form
    valid:boolean
    dirty:boolean
    validate:(val:string|undefined,markAsDirty?:boolean) => void
}

var g_id = 0
function Dropdown<T extends string|number>(props:TDropdownProps<T>, ref?:ForwardedRef<IDropdown<T>>) {
    const {valueRenderer,placeholder:_placeholder,float:_float,onUpdate,readOnly,...menuProps} = props

    const isManaged = props.hasOwnProperty("value")
    const [open,setOpen] = useState<boolean>(false)
    const [selected,setSelected] = useState<T|undefined>(props.value ?? props.defaultValue)
    const [float,setFloat] = useState<boolean>(props.float||false)
    const dropdownId = useRef<string>(`drop_${g_id++}`)
    const {valid,dirty,validate,setValue,setDirty} = useFormControl()
    const timeout = useMemo(() => new Timeout(2000), [])

    const {addOverlay,popOverlay,hasOverlay} = useOverlay()
    
    const current = isManaged ? props.value : selected

    const refSelf = useRef<HTMLDivElement|null>(null)
    const refInput = useRef<HTMLInputElement|null>(null)
    const refMenu = useRef<IMenu<T>|null>(null)
    
    const onMenu = (menu:IMenu<T>|null) => refMenu.current = menu
    
    useImperativeHandle(ref, () => ({
        get value() { return current },
        open(){ !hasOverlay(dropdownId.current) && onToggle() },
        close(){ 
            if (hasOverlay(dropdownId.current)) {
                popOverlay(dropdownId.current)
                onToggle() 
            }
        }, dirty, valid,
        validate(val:string|undefined,markAsDirty=false) { validate(val, markAsDirty) }
    }), [current])

    const _onUpdate = (e:UpdateEvent<T,HTMLDivElement>) => {
        setOpen(false)
        popOverlay(dropdownId.current)
        setValue?.(e.value)
        if (!isManaged) setSelected(e.value)
        onUpdate?.(e)
        if (_float) setFloat(true)
    }

    const onDismissed = () => { 
        props.onDismissed?.()
        setDirty?.(true)
    }
    const onToggle = () => {
        if (hasOverlay(dropdownId.current)) {
            (current===null||current===undefined) && setFloat(false)
            setOpen(false)
        }else{
            const anchor = {follow:dropdownId.current, ...props.anchorProps}
            addOverlay(dropdownId.current, { children:<Menu {...menuProps} onUpdate={_onUpdate} ref={onMenu}/>, anchor, onDismissed})
            refInput.current?.focus()
            setFloat(!!props.float)
            setOpen(true)
        }
    }

    const onClear = (e:React.MouseEvent) => {
        e.stopPropagation()
        setSelected(undefined)
        onUpdate?.(new UpdateEvent<T, HTMLDivElement>(e.nativeEvent, undefined, refSelf.current))
    }

    const onFilter = (e:React.ChangeEvent<HTMLInputElement>) => {
        timeout.next()
        refMenu.current?.filter(e.target.value)
    }

    const onReset = () => { if (refInput.current) refInput.current.value = "" }

    useEffect(() => {
        timeout.subscribe(onReset)
        return () => { 
            open && popOverlay(dropdownId.current)
            timeout.unsubscribe(onReset)
        }
    }, [])

    useEffect(() => {isManaged && setValue?.(props.value)}, [props.value])    
    useEffect(() => {!isManaged && setValue?.(props.defaultValue)}, [props.defaultValue])
    useEffect(() => {props.onValid?.(valid)}, [valid])
    useEffect(() => {props.onDirty?.(dirty)}, [dirty])

    var value:React.ReactNode = null
    const _selected = current!==undefined && current!==null ?  Select.option(props.options, current) : null
    if (_selected) value = props.valueRenderer ? props.valueRenderer(_selected) : Select.text(_selected)
    
    const placeholder = props.placeholder && (!_selected || float) ? <div className={GUI.joinClass("placeholder", float ? "float" : "")}>{props.placeholder}</div> : null

    return <div className={GUI.joinClass(props.className, "dropdown", open ? "open" : "", readOnly ? "readOnly" : "")} ref={refSelf} onClick={onToggle} follow-id={dropdownId.current}>
        <div className="value">{value}</div>
        <div className="flex middle gap-2 endIcons">
            {props.canClear && !!value ? <IconButton className="small" onClick={onClear}><CloseIcon/></IconButton> : null}
            {!props.hideArrow ? <ChevronRight className="arrow"/> : null}
        </div>
        {placeholder}
        <input ref={refInput} onChange={onFilter}/>
    </div>
}

export default forwardRef(Dropdown) as <T extends string|number>(props:TDropdownProps<T>&{ref?:any}) => JSX.Element